import { logOperationalEvent, logWarn } from 'saddlebag-logger';

import { getConsumer } from './cmp-state';
import {
  DEFAULT_ADVERTS_CONSENT,
  DEFAULT_INFORMATION_CONSENT,
} from './constants/consent-defaults';
import {
  COMPONENT,
  CONSENT_WRITE,
  CONSENT_PROPAGATION_FAILURE,
} from './constants/events';
import {
  PREFERENCES,
  IS_GDPR_ACTIVE_FIELD_NAME,
  LATEST_VERSION,
  TC_STRING_DEFAULT_VALUE,
  isRegulatedMarket,
} from './isomorphic';
import { getConsent, willShowConsentBanner } from './ketch-consent';
import {
  getTCString,
  isConsentBannerBeingShown,
  updateConsent,
} from './ketch-consent-api';
import { isOptInJurisdiction } from './ketch-jurisdictions';
import { KETCH_PURPOSE_CODES } from './ketch-types';

import type { TrackingPreferences } from './isomorphic';
import type { KetchConsentData, KetchPurposes } from './ketch-types';

/**
 * Maps Ketch consent data to Saddlebag TrackingPreferences format.
 *
 * When no Ketch consent is available the consent values deny by default in
 * every market - see constants/consent-defaults.ts for the decision record.
 *
 * @param consent - Ketch consent data (or null if no cookie)
 * @param market - Market code for regulated market check
 * @returns TrackingPreferences in Saddlebag format
 */
export function mapKetchConsentToPreferences(
  consent: KetchConsentData | null,
  market: string,
): TrackingPreferences {
  // Deny by default while the traveller's consent state is unknown.
  // See constants/consent-defaults.ts for the decision record.
  if (!consent) {
    return {
      [PREFERENCES.INFORMATION]: DEFAULT_INFORMATION_CONSENT,
      [PREFERENCES.ADVERTS]: DEFAULT_ADVERTS_CONSENT,
      [PREFERENCES.VERSION]: LATEST_VERSION,
      [PREFERENCES.TC_STRING]: TC_STRING_DEFAULT_VALUE,
      // Jurisdiction signal, not a consent value - unchanged by the
      // deny-by-default policy.
      [IS_GDPR_ACTIVE_FIELD_NAME]: isRegulatedMarket(market),
    };
  }

  return {
    [PREFERENCES.INFORMATION]:
      consent.purposes?.[KETCH_PURPOSE_CODES.INFORMATION]?.allowed ?? false,
    [PREFERENCES.ADVERTS]:
      consent.purposes?.[KETCH_PURPOSE_CODES.ADVERTS]?.allowed ?? false,
    [PREFERENCES.VERSION]: LATEST_VERSION,
    // TC string lives in localStorage, not the cookie; getTCString() returns
    // null on SSR/Node (no localStorage) and we fall back to the default.
    [PREFERENCES.TC_STRING]: getTCString() ?? TC_STRING_DEFAULT_VALUE,
    [IS_GDPR_ACTIVE_FIELD_NAME]: isOptInJurisdiction(consent),
  };
}

/**
 * Maps Saddlebag preferences to Ketch purposes format.
 *
 * @param prefs - Saddlebag preferences (partial)
 * @returns Ketch purposes object
 */
export function mapPreferencesToKetchPurposes(
  prefs?: Partial<TrackingPreferences>,
): KetchPurposes {
  if (!prefs) {
    return {};
  }

  const purposes: KetchPurposes = {};

  if (PREFERENCES.INFORMATION in prefs) {
    purposes[KETCH_PURPOSE_CODES.INFORMATION] =
      !!prefs[PREFERENCES.INFORMATION];
  }
  if (PREFERENCES.ADVERTS in prefs) {
    purposes[KETCH_PURPOSE_CODES.ADVERTS] = !!prefs[PREFERENCES.ADVERTS];
  }

  return purposes;
}

/**
 * Gets preferences from Ketch consent cookie.
 * Synchronous read from _swb_consent_ cookie.
 *
 * @param market - Market code for regulated market check
 * @returns TrackingPreferences in Saddlebag format
 */
export function getPreferencesFromKetch(market: string): TrackingPreferences {
  const consent = getConsent();

  return mapKetchConsentToPreferences(consent, market);
}

/**
 * Sets preferences via Ketch.
 * Synchronous return with async Ketch propagation (fire-and-forget).
 *
 * @param prefs - Saddlebag preferences to set
 */
export function setPreferencesViaKetch(
  prefs?: Partial<TrackingPreferences>,
): void {
  const purposes = mapPreferencesToKetchPurposes(prefs);

  logOperationalEvent({
    eventName: CONSENT_WRITE,
    component: COMPONENT,
    consumer: getConsumer(),
  });

  // Fire-and-forget async propagation
  updateConsent({ action: 'set', purposes }).catch((error) => {
    logOperationalEvent({
      eventName: CONSENT_PROPAGATION_FAILURE,
      component: COMPONENT,
      consumer: getConsumer(),
    });
    logWarn({
      component: COMPONENT,
      message: `${CONSENT_PROPAGATION_FAILURE}: ${error instanceof Error ? error.message : 'Unknown error'}`,
    });
  });
}

/**
 * Checks if consent banner should be shown (Ketch mode).
 * Delegates to willShowConsentBanner().
 *
 * @returns true if banner should be shown, false otherwise
 */
export function showBannerViaKetch(): boolean {
  return willShowConsentBanner();
}

/**
 * Checks if GDPR is initialized (Ketch mode).
 * Cookie exists = initialized.
 *
 * @returns true if consent cookie exists, false otherwise
 */
export function isGdprInitialisedViaKetch(): boolean {
  return getConsent() !== null;
}

/**
 * Checks if user is in initial consent session (Ketch mode).
 * Maps to isConsentBannerBeingShown() - banner visible = user making decision.
 *
 * @returns true if banner is currently being shown, false otherwise
 */
export function isGdprInitSessionActiveViaKetch(): boolean {
  return isConsentBannerBeingShown();
}
